/**
 * Currency Context — Auto-detects country via IP, allows manual override.
 * 
 * Supports: USD (USA), CAD (Canada), INR (India)
 * 
 * Usage:
 *   const { currency, symbol, country, setCurrency, loading } = useCurrency();
 *   <CurrencyFlag />  // renders the toggle dropdown
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCIES = {
  usd: { symbol: '$', code: 'USD', flag: '🇺🇸', name: 'USD', country: 'US' },
  cad: { symbol: 'CA$', code: 'CAD', flag: '🇨🇦', name: 'CAD', country: 'CA' },
  inr: { symbol: '₹', code: 'INR', flag: '🇮🇳', name: 'INR', country: 'IN' },
};

const CACHE_KEY = 'ci_currency';
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { 
      const stored = localStorage.getItem(CACHE_KEY);
      return stored && CURRENCIES[stored] ? stored : 'usd';  // Validate against known currencies
    } catch { return 'usd'; }
  });
  const [country, setCountry] = useState('US');
  const [loading, setLoading] = useState(true);
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    // Auto-detect on first load (skip if user already chose)
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached && CURRENCIES[cached]) {
      setCurrencyState(cached);
      setCountry(CURRENCIES[cached].country);
      setLoading(false);
      setDetected(true);
      return;
    }

    fetch(`${API_BASE}/api/pricing/detect`)
      .then(r => r.json())
      .then(data => {
        if (data.currency && CURRENCIES[data.currency]) {
          setCurrencyState(data.currency);
          setCountry(data.country_code || 'US');
          localStorage.setItem(CACHE_KEY, data.currency);
          setDetected(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setCurrency = useCallback((c) => {
    if (CURRENCIES[c]) {
      setCurrencyState(c);
      setCountry(CURRENCIES[c].country);
      localStorage.setItem(CACHE_KEY, c);
    }
  }, []);

  const info = CURRENCIES[currency] || CURRENCIES.usd;

  return (
    <CurrencyContext.Provider value={{
      currency,
      symbol: info.symbol,
      code: info.code,
      flag: info.flag,
      country,
      detected,
      loading,
      setCurrency,
      currencies: CURRENCIES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) return { currency: 'usd', symbol: '$', code: 'USD', flag: '🇺🇸', country: 'US', loading: false, setCurrency: () => {}, currencies: CURRENCIES };
  return ctx;
}

/**
 * Currency flag toggle — dropdown to switch USD / CAD / INR
 * Drop this anywhere in your nav or pricing section.
 */
export function CurrencyFlag({ className = '' }) {
  const { currency, flag, setCurrency, currencies } = useCurrency();
  const [open, setOpen] = useState(false);

  return (
    <div className={`relative inline-block ${className}`} data-testid="currency-flag">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 transition-colors text-sm"
        data-testid="currency-toggle"
      >
        <span className="text-base">{flag}</span>
        <span className="text-xs font-medium text-white/70">{currency.toUpperCase()}</span>
        <svg className="w-3 h-3 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
            {Object.entries(currencies).map(([key, info]) => (
              <button
                key={key}
                onClick={() => { setCurrency(key); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-white/10 transition-colors ${currency === key ? 'bg-white/5 text-[#3EF1C7]' : 'text-white/70'}`}
                data-testid={`currency-option-${key}`}
              >
                <span className="text-base">{info.flag}</span>
                <span className="font-medium">{info.code}</span>
                <span className="text-xs text-white/40 ml-auto">{info.symbol}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default CurrencyProvider;
